import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Select, MenuItem } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

// More data points for a smoother line
export const healthScoreData = [
  { time: '00:00', score: 85 },
  { time: '02:00', score: 83 },
  { time: '04:00', score: 82 },
  { time: '06:00', score: 85 },
  { time: '08:00', score: 88 },
  { time: '10:00', score: 89 },
  { time: '12:00', score: 90 },
  { time: '14:00', score: 89 },
  { time: '16:00', score: 87 },
  { time: '18:00', score: 86 },
  { time: '20:00', score: 85 },
  { time: '22:00', score: 85 },
  { time: '24:00', score: 86 },
];

export const timeframes = [
  { value: 'hour', label: 'Last Hour' },
  { value: 'day', label: 'Last Day' },
  { value: 'week', label: 'Last Week' }
];

// Calculate health score based only on node status (using transformed status values)
const calculateHealthScore = (servers) => {
  if (!servers || servers.length === 0) return 0; // No score if no servers
  
  const totalServers = servers.length;
  const activeServers = servers.filter(server => server.status === 'Active').length;
  
  // Simple calculation: percentage of active nodes
  const healthScore = (activeServers / totalServers) * 100;
  
  return Math.round(healthScore);
};

// Generate health data points based on real server health
const generateHealthData = (servers, timeframe) => {
  const currentHealth = calculateHealthScore(servers);
  
  // Create realistic historical data based on current state
  const dataPoints = [];
  const timeLabels = {
    hour: ['00:00', '10:00', '20:00', '30:00', '40:00', '50:00', 'Now'],
    day: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', 'Now'],
    week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Today']
  };
  
  const labels = timeLabels[timeframe] || timeLabels.day;
  
  labels.forEach((label, index) => {
    // Create stable health trend with minimal realistic variations
    // Systems typically maintain consistent health when active
    let score = currentHealth;
    
    // Add minimal variation only if nodes are active (±2 points max)
    if (currentHealth > 80) {
      const variation = (Math.random() - 0.5) * 4; // ±2 points
      score = Math.max(currentHealth - 5, Math.min(100, currentHealth + variation));
    }
    
    // Ensure last point is current health
    if (index === labels.length - 1) {
      score = currentHealth;
    }
    
    dataPoints.push({ 
      time: label, 
      score: Math.round(score) 
    });
  });
  
  return dataPoints;
};

export default function ServerHealthChart({ selectedTimeframe, setSelectedTimeframe, servers = [] }) {
  const theme = useTheme();
  const healthData = generateHealthData(servers, selectedTimeframe);
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${theme.palette.divider}`,
            p: 1.5,
            borderRadius: 1.5,
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', color: theme.palette.success.main, fontWeight: 600 }}>
            Score: {payload[0].value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
            {payload[0].payload.time}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: 3,
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <CardContent sx={{ 
        p: 0,
        '&:last-child': { pb: 0 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Infrastructure Health
          </Typography>
          <Select
            size="small"
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            sx={{ 
              minWidth: 110, 
              height: 32,
              '& .MuiOutlinedInput-root': { 
                borderRadius: 1.5,
              },
              '& .MuiSelect-select': { 
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
              }
            }}
          >
            {timeframes.map((timeframe) => (
              <MenuItem key={timeframe.value} value={timeframe.value}>
                {timeframe.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        
        <Box sx={{ 
          flex: 1, 
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={healthData}
              margin={{ top: 10, right: 20, bottom: 25, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'} />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 10, fill: theme.palette.text.primary }}
                stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'}
                tickLine={{ stroke: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
                axisLine={{ stroke: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
                dy={5}
              />
              <YAxis
                domain={[70, 100]}
                tick={{ fontSize: 10, fill: theme.palette.text.primary }}
                stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'}
                tickCount={4}
                tickFormatter={(value) => value}
                tickLine={{ stroke: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
                axisLine={{ stroke: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
                orientation="left"
                dx={-5}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={90} stroke={theme.palette.success.main} strokeDasharray="3 3" />
              <ReferenceLine y={80} stroke={theme.palette.warning.main} strokeDasharray="3 3" />
              <ReferenceLine y={75} stroke={theme.palette.error.main} strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="score"
                stroke={theme.palette.success.main}
                strokeWidth={3}
                dot={{ r: 0, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: theme.palette.success.main }}
                animationDuration={1500}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
} 